export const getBrowserWindow = (): (Window & typeof globalThis) | null =>
  globalThis.window === undefined ? null : globalThis.window;

export const getBrowserLocation = (): Location | null => getBrowserWindow()?.location ?? null;

export const getBrowserHistory = (): History | null => getBrowserWindow()?.history ?? null;

export const reloadBrowserLocation = (): void => {
  const location = getBrowserLocation();
  if (location && typeof location.reload === 'function') {
    location.reload();
  }
};

export const getBrowserNavigator = (): Navigator | null => getBrowserWindow()?.navigator ?? null;

export const getBrowserDocument = (): Document | null => getBrowserWindow()?.document ?? null;

export type BrowserWindow = ReturnType<typeof getBrowserWindow>;

export const hasBrowserWindow = (): boolean => getBrowserWindow() !== null;

export const hasBrowserDocument = (): boolean => getBrowserDocument() !== null;

export const dispatchWindowCustomEvent = <Detail = unknown>(eventName: string, detail?: Detail): boolean => {
  const browserWindow = getBrowserWindow();
  if (!browserWindow) {
    return false;
  }

  let CustomEventCtor: typeof CustomEvent | null = null;
  if (typeof browserWindow.CustomEvent === 'function') {
    CustomEventCtor = browserWindow.CustomEvent;
  } else if (typeof CustomEvent === 'function') {
    CustomEventCtor = CustomEvent;
  }

  if (!CustomEventCtor) {
    return false;
  }

  const eventInit = detail === undefined ? undefined : ({ detail } satisfies CustomEventInit<Detail>);
  const event = new CustomEventCtor(eventName, eventInit) as CustomEvent<Detail>;
  return browserWindow.dispatchEvent(event);
};

export type BrowserTimeoutHandle = number | null;

export const setBrowserTimeout = (callback: () => void, delay = 0): BrowserTimeoutHandle => {
  const browserWindow = getBrowserWindow();
  if (!browserWindow) {
    return null;
  }
  const normalizedDelay = Number.isFinite(delay) ? Math.min(Math.max(delay, 0), 2_147_483_647) : 0;
  return browserWindow.setTimeout(callback, normalizedDelay);
};

export const clearBrowserTimeout = (handle: BrowserTimeoutHandle): void => {
  if (handle === null) {
    return;
  }
  const browserWindow = getBrowserWindow();
  if (!browserWindow) {
    return;
  }
  browserWindow.clearTimeout(handle);
};
